/**
 * User Activity Log — "who did what, and when".
 *
 * Built from data that ALREADY exists:
 *   • transactions  → createdBy/createdAt, updatedBy/updatedAt, postedBy/postedAt
 *                     (gives Created / Edited / Posted for EVERY voucher type)
 *   • auditLog      → master-data adds/edits and ALL document deletions
 *                     (deleteTransaction writes here centrally)
 *
 * Risk flags: Backdated · Edited-after-posting · Deleted.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { colors } from '../../theme/colors';
import { TRAN_TYPES } from '../../config/tranTypes';
import { TRAN_TYPE_ROUTES as DOC_ROUTE } from '../../utils/navigationHelper';
import {
  FiActivity, FiUser, FiEdit2, FiTrash2, FiDownload, FiPrinter, FiSearch, FiAlertTriangle,
} from 'react-icons/fi';
import {
  SummaryCards, SummaryCard, SummaryIcon, SummaryInfo, SummaryLabel, SummaryValue,
  FiltersCard, FiltersRow, FilterGroup, Label, Input, Select, ButtonGroup, Button,
  ContentCard, TableContainer, Table, Th, Td, Tr, EmptyState, SearchWrap,
  printReport, downloadCsv, csvCell, fmtDateTime,
} from './auditReportStyles';
import styled from 'styled-components';

// DOC_ROUTE is the shared navigationHelper map (imported above as an alias) so every
// document type opens the RIGHT page — the old local map sent JV to a dead route and
// omitted CB/REQ/PRO/MIP/PRC/DEP/FAD.

const ACTIONS = ['Created', 'Edited', 'Posted', 'Cancelled', 'Deleted'];
const BACKDATE_DAYS = 7; // entered more than a week after the document date

const dayDiff = (aIso, bIso) => {
  if (!aIso || !bIso) return 0;
  const a = new Date(String(aIso).slice(0, 10));
  const b = new Date(String(bIso).slice(0, 10));
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((a - b) / 86400000);
};

const typeName = (code) => TRAN_TYPES[code]?.name || code;

const UserActivityLog = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('user-activity-log');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const companyId = activeCompany?.id;

  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState(() => savedMem?.events ?? []);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(() => savedMem?.generated ?? false);

  // Filters
  const [from, setFrom] = useState(() => savedMem?.from ?? '');
  const [to, setTo] = useState(() => savedMem?.to ?? '');
  const [userId, setUserId] = useState(() => savedMem?.userId ?? 'all');
  const [action, setAction] = useState(() => savedMem?.action ?? 'all');
  const [docType, setDocType] = useState(() => savedMem?.docType ?? 'all');
  const [flag, setFlag] = useState(() => savedMem?.flag ?? 'all'); // all | backdated | editedAfterPost | deleted
  const [minAmt, setMinAmt] = useState(() => savedMem?.minAmt ?? '');
  const [maxAmt, setMaxAmt] = useState(() => savedMem?.maxAmt ?? '');
  const [search, setSearch] = useState(() => savedMem?.search ?? '');

  // ── Users (for names + the user filter) ──
  useEffect(() => {
    if (!companyId) return;
    getDocs(collection(db, 'companies', companyId, 'users'))
      .then(s => setUsers(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(console.error);
  }, [companyId]);

  const userName = useCallback((uid) => {
    if (!uid) return 'Unknown';
    const u = users.find(x => x.id === uid);
    return u?.displayName || u?.name || u?.email || 'Unknown';
  }, [users]);

  // ── Build the timeline ──
  const generate = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setGenerated(true);
    try {
      const fyId = activeFinancialYear?.id;
      const [txnSnap, logSnap] = await Promise.all([
        getDocs(fyId
          ? query(collection(db, 'companies', companyId, 'transactions'), where('financialYearId', '==', fyId))
          : collection(db, 'companies', companyId, 'transactions')),
        // Most-recent 1000 audit rows only (the log grows forever; the report
        // client-sorts by this same field, so the visible result is unchanged
        // for companies under 1000 rows).
        getDocs(query(
          collection(db, 'companies', companyId, 'auditLog'),
          orderBy('timestamp', 'desc'),
          limit(1000)
        )).catch(() => ({ docs: [] })),
      ]);
      recordReads(companyId, txnSnap.size + (logSnap.size || 0));

      const list = [];
      const inBranch = (b) => !activeBranch || activeBranch.id === 'all' || b === activeBranch.id;

      // 1) Document activity — straight out of the transaction's own stamps.
      txnSnap.docs.forEach(d => {
        const t = { id: d.id, ...d.data() };
        if (!inBranch(t.branchId)) return;

        const base = {
          docId: t.id,
          code: t.tranTypeCode,
          type: typeName(t.tranTypeCode),
          reference: t.docNumber || '',
          party: t.partyName || '',
          amount: Number(t.totalAmount) || 0,
          branchName: t.branchName || '',
          docDate: t.docDate || '',
          kind: 'doc',
        };

        // Entered long after the document date → backdated.
        const backdated = t.createdAt && t.docDate && dayDiff(t.createdAt, t.docDate) > BACKDATE_DAYS;

        if (t.createdAt) {
          list.push({
            ...base, at: t.createdAt, by: t.createdBy, action: 'Created',
            flags: backdated ? ['backdated'] : [],
          });
        }
        if (t.postedAt && t.postedBy) {
          list.push({ ...base, at: t.postedAt, by: t.postedBy, action: 'Posted', flags: [] });
        }
        // An edit only counts when the doc actually changed after creation.
        if (t.updatedAt && t.createdAt && t.updatedAt !== t.createdAt) {
          const afterPost = t.postedAt && t.updatedAt > t.postedAt;
          list.push({
            ...base, at: t.updatedAt, by: t.updatedBy || t.createdBy, action: 'Edited',
            flags: afterPost ? ['editedAfterPost'] : [],
          });
        }
      });

      // 2) Audit log — deletions (all documents) + master-data changes.
      (logSnap.docs || []).forEach(d => {
        const l = { id: d.id, ...d.data() };
        const det = (l.details && typeof l.details === 'object') ? l.details : {};
        if (!inBranch(det.branchId)) return;

        const act = String(l.action || '').toLowerCase();
        const actionLabel = act === 'delete' ? 'Deleted'
          : act === 'create' || act === 'add' ? 'Created'
          : act === 'update' || act === 'edit' ? 'Edited'
          : act === 'cancel' ? 'Cancelled'
          : (l.action || 'Activity');

        // Documents deleted via deleteTransaction land here with a tranTypeCode.
        const isDoc = !!TRAN_TYPES[l.entityType];

        list.push({
          docId: l.entityId,
          code: isDoc ? l.entityType : null,
          type: isDoc ? typeName(l.entityType) : (l.entityType || 'Record'),
          reference: det.docNumber || det.name || l.entityId || '',
          party: det.partyName || '',
          amount: Number(det.amount) || 0,
          branchName: det.branchName || '',
          docDate: det.docDate || '',
          at: l.timestamp,
          by: l.performedBy,
          action: actionLabel,
          flags: actionLabel === 'Deleted' ? ['deleted'] : [],
          kind: isDoc ? 'doc' : 'master',
          note: det.note || (typeof l.details === 'string' ? l.details : ''),
        });
      });

      list.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
      setEvents(list);
    } catch (e) {
      console.error('User activity load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId, activeFinancialYear, activeBranch]);

  // Persist snapshot so Back restores the exact view (display-only: filters live in the useMemo below).
  useEffect(() => {
    if (!generated) return;
    saveMemory({
      events, generated: true,
      from, to, userId, action, docType, flag, minAmt, maxAmt, search,
    });
  }, [generated, events, from, to, userId, action, docType, flag, minAmt, maxAmt, search, saveMemory]);

  // ── Filters ──
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = Number(minAmt) || 0;
    const max = Number(maxAmt) || 0;
    return events.filter(e => {
      const day = String(e.at || '').slice(0, 10);
      if (from && day < from) return false;
      if (to && day > to) return false;
      if (userId !== 'all' && e.by !== userId) return false;
      if (action !== 'all' && e.action !== action) return false;
      if (docType !== 'all' && e.code !== docType) return false;
      if (flag !== 'all' && !(e.flags || []).includes(flag)) return false;
      if (min && e.amount < min) return false;
      if (max && e.amount > max) return false;
      if (q && !(`${e.reference} ${e.party} ${e.type} ${userName(e.by)}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [events, from, to, userId, action, docType, flag, minAmt, maxAmt, search, userName]);

  const summary = useMemo(() => {
    const byUser = {};
    let edits = 0, deletes = 0;
    rows.forEach(e => {
      byUser[e.by] = (byUser[e.by] || 0) + 1;
      if (e.action === 'Edited') edits++;
      if (e.action === 'Deleted') deletes++;
    });
    const top = Object.entries(byUser).sort((a, b) => b[1] - a[1])[0];
    return { total: rows.length, topUser: top ? userName(top[0]) : '—', edits, deletes };
  }, [rows, userName]);

  const docTypeOptions = useMemo(
    () => [...new Set(events.map(e => e.code).filter(Boolean))].sort((a, b) => typeName(a).localeCompare(typeName(b))),
    [events]
  );

  const openDoc = (e) => {
    if (!e.code || !e.docId || e.action === 'Deleted') return; // a deleted doc can't be opened
    const route = DOC_ROUTE[e.code];
    if (route) navigate(route, { state: { viewTransactionId: e.docId } });
  };

  const handleExport = () => {
    let csv = 'Date & Time,User,Action,Type,Reference,Party,Amount,Branch,Flags\n';
    rows.forEach(e => {
      csv += [
        csvCell(fmtDateTime(e.at)), csvCell(userName(e.by)), csvCell(e.action), csvCell(e.type),
        csvCell(e.reference), csvCell(e.party), e.amount || 0, csvCell(e.branchName),
        csvCell((e.flags || []).join(' ')),
      ].join(',') + '\n';
    });
    downloadCsv(`User_Activity_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handlePrint = () => {
    const body = rows.map(e => `<tr>
      <td>${fmtDateTime(e.at)}</td><td>${userName(e.by)}</td>
      <td>${e.action}</td><td>${e.type}</td><td>${e.reference}</td>
      <td>${e.party || '-'}</td>
      <td class="r">${e.amount ? formatCurrency(e.amount) : '-'}</td>
      <td class="${(e.flags || []).length ? 'short' : ''}">${(e.flags || []).join(', ') || '-'}</td>
    </tr>`).join('');
    printReport('User Activity Log',
      `${activeCompany?.companyName || ''} | ${from || 'All'} to ${to || 'All'} | ${rows.length} activities`,
      `<table><thead><tr><th>Date &amp; Time</th><th>User</th><th>Action</th><th>Type</th>
        <th>Reference</th><th>Party</th><th class="r">Amount</th><th>Flags</th>
       </tr></thead><tbody>${body}</tbody></table>`);
  };

  return (
    <PageLayout pageTitle="User Activity Log" breadcrumb="Reports / Main Reports / User Activity Log">
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiActivity /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Total Activities</SummaryLabel><SummaryValue>{summary.total}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><FiUser /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Most Active User</SummaryLabel>
            <SummaryValue title={summary.topUser}>{summary.topUser}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#E65100"><FiEdit2 /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Edits</SummaryLabel><SummaryValue>{summary.edits}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}><FiTrash2 /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Deletions</SummaryLabel>
            <SummaryValue style={{ color: summary.deletes ? colors.error.main : undefined }}>{summary.deletes}</SummaryValue></SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <FiltersCard>
        <FiltersRow>
          <FilterGroup><Label>From Date</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></FilterGroup>
          <FilterGroup><Label>To Date</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></FilterGroup>
          <FilterGroup>
            <Label>User</Label>
            <Select value={userId} onChange={e => setUserId(e.target.value)}>
              <option value="all">All Users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.displayName || u.name || u.email}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Action</Label>
            <Select value={action} onChange={e => setAction(e.target.value)}>
              <option value="all">All Actions</option>
              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Document Type</Label>
            <Select value={docType} onChange={e => setDocType(e.target.value)}>
              <option value="all">All Types</option>
              {docTypeOptions.map(c => <option key={c} value={c}>{typeName(c)}</option>)}
            </Select>
          </FilterGroup>
        </FiltersRow>

        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup>
            <Label>Risk Flag</Label>
            <Select value={flag} onChange={e => setFlag(e.target.value)}>
              <option value="all">All Activity</option>
              <option value="deleted">🚩 Deleted only</option>
              <option value="editedAfterPost">🚩 Edited after posting</option>
              <option value="backdated">🚩 Backdated entries</option>
            </Select>
          </FilterGroup>
          <FilterGroup><Label>Min Amount</Label><Input type="number" value={minAmt} onChange={e => setMinAmt(e.target.value)} placeholder="0" /></FilterGroup>
          <FilterGroup><Label>Max Amount</Label><Input type="number" value={maxAmt} onChange={e => setMaxAmt(e.target.value)} placeholder="Any" /></FilterGroup>
          <FilterGroup style={{ minWidth: 240 }}>
            <Label>Search</Label>
            <SearchWrap>
              <FiSearch size={14} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Doc no, party, user, type…" />
            </SearchWrap>
          </FilterGroup>
          <ButtonGroup>
            <Button onClick={generate} disabled={loading} style={{ background: colors.primary.main, color: '#fff', borderColor: colors.primary.main }}>
              <FiActivity /> {loading ? 'Loading…' : 'Generate'}
            </Button>
            <Button onClick={handleExport} disabled={!rows.length}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!rows.length}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? <EmptyState>Loading…</EmptyState>
            : !generated ? <EmptyState>Choose your filters and click <b>Generate</b>.</EmptyState>
            : rows.length === 0 ? <EmptyState>No activity found for these filters.</EmptyState> : (
              <Table>
                <thead><tr>
                  <Th>Date &amp; Time</Th><Th>User</Th><Th>Action</Th><Th>Type</Th>
                  <Th>Reference</Th><Th>Party / Details</Th>
                  <Th align="right">Amount</Th><Th>Branch</Th><Th>Flags</Th>
                </tr></thead>
                <tbody>
                  {rows.map((e, i) => (
                    <Tr key={`${e.docId}_${e.action}_${e.at}_${i}`}>
                      <Td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.at)}</Td>
                      <Td><b>{userName(e.by)}</b></Td>
                      <Td><ActionChip $a={e.action}>{e.action}</ActionChip></Td>
                      <Td>{e.type}</Td>
                      <Td>
                        {e.code && e.action !== 'Deleted' && DOC_ROUTE[e.code] ? (
                          <LinkText onClick={() => openDoc(e)}>{e.reference || '—'}</LinkText>
                        ) : (e.reference || '—')}
                      </Td>
                      <Td>{e.party || e.note || <span style={{ color: '#bbb' }}>—</span>}</Td>
                      <Td align="right">{e.amount ? formatCurrency(e.amount) : '—'}</Td>
                      <Td>{e.branchName || '—'}</Td>
                      <Td>
                        {(e.flags || []).map(f => (
                          <Flag key={f} title={
                            f === 'backdated' ? `Entered more than ${BACKDATE_DAYS} days after the document date`
                              : f === 'editedAfterPost' ? 'This document was changed AFTER it was posted'
                              : 'This document was deleted'
                          }>
                            <FiAlertTriangle size={10} />
                            {f === 'backdated' ? 'Backdated' : f === 'editedAfterPost' ? 'Edited after posting' : 'Deleted'}
                          </Flag>
                        ))}
                        {(e.flags || []).length === 0 && <span style={{ color: '#ddd' }}>—</span>}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

const ActionChip = styled.span`
  display: inline-block; padding: 3px 9px; border-radius: 12px;
  font-size: 11px; font-weight: 700;
  background: ${p => p.$a === 'Deleted' ? '#FFEBEE' : p.$a === 'Edited' ? '#FFF3E0' : p.$a === 'Posted' ? '#E8F5E9' : '#E3F2FD'};
  color: ${p => p.$a === 'Deleted' ? '#C62828' : p.$a === 'Edited' ? '#E65100' : p.$a === 'Posted' ? '#2E7D32' : '#1565C0'};
`;
const LinkText = styled.span`
  color: #1a73e8; cursor: pointer; font-weight: 600;
  &:hover { text-decoration: underline; }
`;
const Flag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  margin-right: 5px; padding: 2px 7px; border-radius: 4px;
  background: #FFEBEE; color: #C62828; font-size: 10px; font-weight: 700;
  white-space: nowrap;
`;

export default UserActivityLog;
